import React, { useState, useEffect, useRef } from "react";
import "bulma/css/bulma.min.css";
import "../../../styles/egresos.css";
import { useEmpresaSelector } from "../../../context/EmpresaSelectorContext";
import { apiRequest } from "../../../utils/api";
import * as XLSX from "xlsx";

export default function Egresos() {
  const { empresaSeleccionada, esConexAdmin, empresas } = useEmpresaSelector();
  const [egresos, setEgresos] = useState([]);
  const [cuentasPorPagar, setCuentasPorPagar] = useState([]);
  const [filtroFecha, setFiltroFecha] = useState(""); // Vacío por defecto para mostrar todas
  const [loading, setLoading] = useState(true);
  const [loadingCuentas, setLoadingCuentas] = useState(true);
  const [error, setError] = useState("");
  const [errorCuentas, setErrorCuentas] = useState("");

  // Modal para crear en todas las empresas
  const [showAllEmpresasModal, setShowAllEmpresasModal] = useState(false);
  const pendingCreationRef = useRef(null);


  const [nuevoEgreso, setNuevoEgreso] = useState({
    descripcion: "",
    subtotal: "",
    iva: "",
    numeroComprobante: "",
    tipoPago: "efectivo",
    banco: "",
    categoria: "productos",
    fecha: new Date().toISOString().split('T')[0],
  });

  const [nuevaCuenta, setNuevaCuenta] = useState({
    fechaFactura: new Date().toISOString().split('T')[0],
    numeroFactura: "",
    cliente: "", // Comercial
    letraChica: "", // usaremos para guardar tipoPago/banco/comprobante
    importeTotal: "", // Subtotal
    ivaPorcentaje: "0", // IVA editable (%)
    fechaVencimiento: new Date().toISOString().split('T')[0],
    saldoAdeudado: "", // Total (auto)
    pago1: "",
    pago2: "",
    pago3: "",
    pago4: "",
    pago5: "",
    pago6: "",
    numeroPagos: 1,
    tipoPago: 'efectivo',
    banco: '',
    comprobante: '',
    modoPagosAutomatico: true, // Nuevo: modo automático vs manual
  });

  // Helpers IVA y Total para nuevaCuenta
  const ivaMonto = (() => {
    const sub = parseFloat(nuevaCuenta.importeTotal || 0);
    const pct = parseFloat(nuevaCuenta.ivaPorcentaje || 0) / 100;
    const val = sub * pct;
    return Number.isFinite(val) ? val : 0;
  })();
  const totalCalc = (() => {
    const sub = parseFloat(nuevaCuenta.importeTotal || 0);
    const tot = sub + ivaMonto;
    return Number.isFinite(tot) ? tot : 0;
  })();

  // Calcular pagos iguales por numeroPagos
  const calcularPagosIguales = (total, n) => {
    const cuotas = Math.max(1, Math.min(6, parseInt(n || '1')));
    const monto = Math.max(0, Number(total || 0));
    const base = Math.floor((monto / cuotas) * 1000) / 1000;
    let resto = Math.round(monto * 1000 - base * 1000 * cuotas);
    const arr = Array.from({ length: cuotas }, () => base);
    for (let i = 0; i < cuotas && resto > 0; i++) { arr[i] = Number((arr[i] + 0.001).toFixed(3)); resto--; }
    return arr.map(v => v.toFixed(3));
  };

  useEffect(() => {
    const cuotas = Math.max(1, Math.min(6, parseInt(nuevaCuenta.numeroPagos || '1')));

    setNuevaCuenta(prev => {
      const updated = {
        ...prev,
        saldoAdeudado: totalCalc.toFixed(3),
        numeroPagos: cuotas,
      };

      // Solo calcular pagos automáticamente si está en modo automático
      if (prev.modoPagosAutomatico) {
        const pagos = calcularPagosIguales(totalCalc, cuotas);
        updated.pago1 = pagos[0] || '';
        updated.pago2 = pagos[1] || '';
        updated.pago3 = pagos[2] || '';
        updated.pago4 = pagos[3] || '';
        updated.pago5 = pagos[4] || '';
        updated.pago6 = pagos[5] || '';
      }

      return updated;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nuevaCuenta.numeroPagos, nuevaCuenta.importeTotal, nuevaCuenta.ivaPorcentaje]);
  const [editEgresoId, setEditEgresoId] = useState(null);
  const [editCuentaId, setEditCuentaId] = useState(null);

  // Cargar egresos desde el backend
  const cargarEgresos = async () => {
    try {
      setLoading(true);
      let endpoint = '/egresos';
      if (empresaSeleccionada && esConexAdmin && empresaSeleccionada.id !== 'all') {
        endpoint += `?empresaId=${empresaSeleccionada.id}`;
      }
      const response = await apiRequest(endpoint);
      if (!response.ok) {
        let errorData;
        try {
          errorData = await response.json();
        } catch (e) {
          errorData = { error: 'Respuesta no válida del servidor' };
        }
        console.error('❌ Error al cargar egresos:', errorData);
        throw new Error(`Error al cargar egresos: ${errorData.error || response.statusText}`);
      }

      const data = await response.json();
      setEgresos(data);
      setError(""); // Limpiar errores previos
    } catch (error) {
      console.error('💥 Error cargando egresos:', error);
      setError(`Error al cargar los egresos: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Cargar cuentas por pagar desde el backend
  const cargarCuentasPorPagar = async () => {
    try {
      setLoadingCuentas(true);
      let endpoint = '/cuentas-por-pagar';
      if (empresaSeleccionada && esConexAdmin && empresaSeleccionada.id !== 'all') {
        endpoint += `?empresaId=${empresaSeleccionada.id}`;
      }
      const response = await apiRequest(endpoint);
      if (!response.ok) throw new Error('Error al cargar cuentas por pagar');
      const data = await response.json();
      setCuentasPorPagar(data);
    } catch (error) {
      console.error('Error cargando cuentas por pagar:', error);
      setErrorCuentas('Error al cargar las cuentas por pagar');
    } finally {
      setLoadingCuentas(false);
    }
  };

  // Cargar datos solo si hay empresa seleccionada
  useEffect(() => {
    if (empresaSeleccionada) {
      cargarEgresos();
      cargarCuentasPorPagar();
    } else {
    }
  }, [empresaSeleccionada?.id]);

  // Filtrar por fecha
  // Función auxiliar para comparar fechas ignorando la hora y zona horaria
  const esMismoDia = (fecha1, fecha2) => {
    if (!fecha1 || !fecha2) return false;

    // Extraer solo la parte de fecha (YYYY-MM-DD) para comparación
    const fecha1Str = fecha1.split('T')[0];
    const fecha2Str = fecha2.split('T')[0];

    return fecha1Str === fecha2Str;
  };

  const egresosFiltrados = egresos.filter(e => 
    !filtroFecha || (e.fecha && esMismoDia(e.fecha, filtroFecha))
  );

  const cuentasPorPagarFiltradas = cuentasPorPagar.filter(c => 
    !filtroFecha || ((c.fechaFactura || c.fecha) && esMismoDia(c.fechaFactura || c.fecha, filtroFecha))
  );

  // Crear nuevo egreso
  const handleAddEgreso = async (e) => {
    e.preventDefault();

    // Validar que se haya seleccionado una empresa específica
    if (!empresaSeleccionada || empresaSeleccionada.id === 'all') {
      if (esConexAdmin && empresaSeleccionada?.id === 'all') {
        // Mostrar modal de confirmación
        pendingCreationRef.current = { type: 'egreso' };
        setShowAllEmpresasModal(true);
        return;
      }
      setError("Debe seleccionar una empresa específica para crear egresos");
      return;
    }

    if (!nuevoEgreso.descripcion || !nuevoEgreso.subtotal || nuevoEgreso.subtotal <= 0) {
      setError("Todos los campos obligatorios deben estar completos y el subtotal debe ser mayor a 0");
      return;
    }
    if (nuevoEgreso.tipoPago === 'transferencia' && !nuevoEgreso.banco) {
      setError("Debe especificar un banco para transferencia");
      return;
    }

    try {
      const subtotalNum = parseFloat(nuevoEgreso.subtotal);
      const ivaPct = parseFloat(nuevoEgreso.iva || 0);
      const ivaNum = subtotalNum * (ivaPct / 100);
      const totalNum = subtotalNum + ivaNum;
      const egresoData = {
        descripcion: `${nuevoEgreso.descripcion} | Subtotal: $${subtotalNum.toFixed(3)} | IVA: ${ivaPct.toFixed(2)}% ($${ivaNum.toFixed(2)}) | #Comprobante: ${nuevoEgreso.numeroComprobante || '-'}`,
        monto: parseFloat(totalNum.toFixed(2)),
        fecha: new Date(nuevoEgreso.fecha).toISOString(),
        tipoPago: nuevoEgreso.tipoPago,
        banco: nuevoEgreso.banco || null,
        categoria: nuevoEgreso.categoria,
        empresaId: empresaSeleccionada?.id || null,
      };

      const response = await apiRequest('/egresos', {
        method: 'POST',
        body: JSON.stringify(egresoData),
      });

      if (!response.ok) throw new Error((await response.json()).error || 'Error al crear egreso');

      await cargarEgresos();
      setNuevoEgreso({
        descripcion: "",
        subtotal: "",
        iva: "",
        numeroComprobante: "",
        tipoPago: "efectivo",
        banco: "",
        categoria: "productos",
        fecha: new Date().toISOString().split('T')[0],
      });
      setEditEgresoId(null);
      setError("");
    } catch (error) {
      console.error('Error creando egreso:', error);
      setError('Error al crear el egreso');
    }
  };

  // Editar egreso
  const handleEditEgreso = (egreso) => {
    setEditEgresoId(egreso.id);
    const matchSubtotal = egreso.descripcion?.match(/Subtotal: \$(\d+\.?\d*)/);
    const matchIvaPct = egreso.descripcion?.match(/IVA: (\d+\.?\d*)%/);
    const matchIvaMonto = egreso.descripcion?.match(/IVA: \$(\d+\.?\d*)/);
    let subtotal = matchSubtotal ? matchSubtotal[1] : "";
    let iva = "0";
    if (matchIvaPct) {
      iva = matchIvaPct[1];
    } else if (matchIvaMonto) {
      const monto = parseFloat(matchIvaMonto[1]);
      iva = subtotal ? ((monto / parseFloat(subtotal)) * 100).toFixed(2) : "0";
    }
    if (!subtotal) {
      // Calcular subtotal si no está en descripción
      const total = parseFloat(egreso.monto?.toString() || "0");
      if (matchIvaMonto) {
        // Para egresos antiguos con IVA en $, subtotal = total - ivaMonto
        const ivaMonto = parseFloat(matchIvaMonto[1]);
        subtotal = (total - ivaMonto).toFixed(3);
      } else {
        // Para otros casos, subtotal = total / (1 + iva%)
        const ivaPct = parseFloat(iva) / 100;
        subtotal = (total / (1 + ivaPct)).toFixed(3);
      }
    }
    const matchComp = egreso.descripcion?.match(/#Comprobante: ([^|]+)/);
    const numeroComprobante = matchComp ? matchComp[1].trim() : "";
    setNuevoEgreso({
      descripcion: egreso.descripcion.split('|')[0]?.trim() || egreso.descripcion,
      subtotal,
      iva,
      numeroComprobante,
      tipoPago: egreso.tipoPago || "efectivo",
      banco: egreso.banco || "",
      categoria: egreso.categoria || "productos",
      fecha: egreso.fecha ? egreso.fecha.split('T')[0] : new Date().toISOString().split('T')[0],
    });
  };

  // Actualizar egreso
  const handleUpdateEgreso = async (e) => {
    e.preventDefault();
    if (!nuevoEgreso.descripcion || !nuevoEgreso.subtotal || nuevoEgreso.subtotal <= 0) {
      setError("Todos los campos obligatorios deben estar completos y el subtotal debe ser mayor a 0");
      return;
    }
    if (nuevoEgreso.tipoPago === 'transferencia' && !nuevoEgreso.banco) {
      setError("Debe especificar un banco para transferencia");
      return;
    }

    try {
      const subtotalNum = parseFloat(nuevoEgreso.subtotal);
      const ivaPct = parseFloat(nuevoEgreso.iva || 0);
      const ivaNum = subtotalNum * (ivaPct / 100);
      const totalNum = subtotalNum + ivaNum;
      const response = await apiRequest(`/egresos/${editEgresoId}`, {
        method: 'PUT',
        body: JSON.stringify({
          descripcion: `${nuevoEgreso.descripcion} | Subtotal: $${subtotalNum.toFixed(3)} | IVA: ${ivaPct.toFixed(2)}% ($${ivaNum.toFixed(2)}) | #Comprobante: ${nuevoEgreso.numeroComprobante || '-'}`,
          monto: parseFloat(totalNum.toFixed(2)),
          fecha: new Date(nuevoEgreso.fecha).toISOString(),
          tipoPago: nuevoEgreso.tipoPago,
          banco: nuevoEgreso.banco || null,
          categoria: nuevoEgreso.categoria,
          empresaId: empresaSeleccionada?.id || null,
        }),
      });

      if (!response.ok) throw new Error((await response.json()).error || 'Error al actualizar egreso');

      await cargarEgresos();
      setNuevoEgreso({
        descripcion: "",
        subtotal: "",
        iva: "",
        numeroComprobante: "",
        tipoPago: "efectivo",
        banco: "",
        categoria: "productos",
        fecha: new Date().toISOString().split('T')[0],
      });
      setEditEgresoId(null);
      setError("");
    } catch (error) {
      console.error('Error actualizando egreso:', error);
      setError('Error al actualizar el egreso');
    }
  };

  // Eliminar egreso
  const handleDeleteEgreso = async (id) => {
    // Validar que el ID sea válido
    const numericId = parseInt(id);
    if (!numericId || isNaN(numericId)) {
      console.error('ID de egreso inválido:', id, typeof id);
      setError('ID de egreso inválido');
      return;
    }

    if (!window.confirm('¿Estás seguro de que quieres eliminar este egreso? Esta acción no se puede deshacer.')) return;

    try {
      const response = await apiRequest(`/egresos/${numericId}`, { method: 'DELETE' });
      if (!response.ok) {
        let errorData;
        try {
          errorData = await response.json();
        } catch (e) {
          errorData = { error: 'Respuesta no válida del servidor' };
        }
        console.error('Error del servidor:', errorData);

        // Manejar errores específicos
        if (response.status === 404) {
          throw new Error('El egreso no existe o ya fue eliminado');
        } else if (response.status === 403) {
          throw new Error('No tienes permisos para eliminar este egreso. Verifica que pertenezca a tu empresa.');
        } else if (response.status === 401) {
          throw new Error('Sesión expirada. Por favor, inicia sesión nuevamente');
        } else {
          throw new Error(errorData.error || `Error ${response.status}: ${response.statusText}`);
        }
      }

      const result = await response.json();
      await cargarEgresos();
      setError(""); // Limpiar errores previos

      // Mostrar mensaje de éxito
      alert('Egreso eliminado exitosamente');
    } catch (error) {
      console.error('Error eliminando egreso:', error);
      setError(`Error al eliminar el egreso: ${error.message}`);
    }
  };

  // Cancelar edición
  const handleCancelEdit = () => {
    setEditEgresoId(null);
    setNuevoEgreso({
      descripcion: "",
      subtotal: "",
      iva: "",
      numeroComprobante: "",
      tipoPago: "efectivo",
      banco: "",
      categoria: "productos",
      fecha: new Date().toISOString().split('T')[0],
    });
    setError("");
  };

  // Crear nueva cuenta por pagar
  const handleAddCuenta = async (e) => {
    e.preventDefault();

    // Validar que se haya seleccionado una empresa específica
    if (!empresaSeleccionada || empresaSeleccionada.id === 'all') {
      if (esConexAdmin && empresaSeleccionada?.id === 'all') {
        // Mostrar modal de confirmación
        pendingCreationRef.current = { type: 'cuentaPorPagar' };
        setShowAllEmpresasModal(true);
        return;
      }
      setErrorCuentas("Debe seleccionar una empresa específica para crear cuentas por pagar");
      return;
    }

    if (!nuevaCuenta.numeroFactura || !nuevaCuenta.cliente || !nuevaCuenta.importeTotal || nuevaCuenta.importeTotal <= 0) {
      setErrorCuentas("Los campos obligatorios deben estar completos y el subtotal debe ser mayor a 0");
      return;
    }

    // Validar fechas
    if (!nuevaCuenta.fechaFactura) {
      setErrorCuentas("La fecha de factura es obligatoria");
      return;
    }

    if (!nuevaCuenta.fechaVencimiento) {
      setErrorCuentas("La fecha de vencimiento es obligatoria");
      return;
    }

    // Validar que importeTotal sea un número válido
    const importeTotalNum = parseFloat(nuevaCuenta.importeTotal);
    if (isNaN(importeTotalNum) || importeTotalNum <= 0) {
      setErrorCuentas("El subtotal debe ser un número válido mayor a 0");
      return;
    }

    // Validar que el total calculado sea válido
    if (isNaN(totalCalc) || totalCalc <= 0) {
      setErrorCuentas("El total calculado debe ser mayor a 0");
      return;
    }

    // Validar suma de pagos en modo manual
    if (!nuevaCuenta.modoPagosAutomatico) {
      const sumaPagos = [1,2,3,4,5,6].reduce((sum, i) => {
        const pago = nuevaCuenta[`pago${i}`];
        return sum + (pago ? parseFloat(pago) : 0);
      }, 0);

      const totalAdeudado = parseFloat(totalCalc.toFixed(3));
      if (Math.abs(sumaPagos - totalAdeudado) > 0.01) {
        setErrorCuentas(`La suma de los pagos ($${sumaPagos.toFixed(3)}) no coincide con el total adeudado ($${totalAdeudado.toFixed(3)})`);
        return;
      }
    }

    try {
      // Empaquetar tipoPago/banco/comprobante en letraChica
      const meta = `Tipo:${nuevaCuenta.tipoPago}${nuevaCuenta.tipoPago==='transferencia' ? `, Banco:${nuevaCuenta.banco || 'N/A'}`: ''}${nuevaCuenta.comprobante ? `, Comprobante:${nuevaCuenta.comprobante}`:''}`;

      // Función auxiliar para convertir pagos a números válidos o undefined
      const convertirPago = (pago) => {
        if (!pago || pago === '' || pago === '0') return undefined;
        const num = parseFloat(pago);
        return isNaN(num) || num <= 0 ? undefined : num;
      };

      const datosAEnviar = {
        fechaFactura: nuevaCuenta.fechaFactura,
        numeroFactura: nuevaCuenta.numeroFactura,
        cliente: nuevaCuenta.cliente,
        letraChica: `${meta} | Subtotal: $${parseFloat(nuevaCuenta.importeTotal).toFixed(3)} | IVA: ${parseFloat(nuevaCuenta.ivaPorcentaje || 0).toFixed(2)}% (${ivaMonto.toFixed(2)}$) | #Comprobante: ${nuevaCuenta.numeroComprobante || '-'}`,
        importeTotal: parseFloat(nuevaCuenta.importeTotal),
        fechaVencimiento: nuevaCuenta.fechaVencimiento,
        saldoAdeudado: parseFloat(totalCalc.toFixed(3)),
        pago1: convertirPago(nuevaCuenta.pago1),
        pago2: convertirPago(nuevaCuenta.pago2),
        pago3: convertirPago(nuevaCuenta.pago3),
        pago4: convertirPago(nuevaCuenta.pago4),
        pago5: convertirPago(nuevaCuenta.pago5),
        pago6: convertirPago(nuevaCuenta.pago6),
        empresaId: empresaSeleccionada?.id,
      };

      // Debug: mostrar fecha que se está guardando
      const response = await apiRequest('/cuentas-por-pagar', {
        method: 'POST',
        body: JSON.stringify(datosAEnviar),
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Error desconocido' }));
        console.error('❌ Error del backend:', errorData);
        throw new Error(errorData.error || `Error ${response.status}: ${response.statusText}`);
      }

      const result = await response.json();
      await cargarCuentasPorPagar();
      setNuevaCuenta({
        fechaFactura: new Date().toISOString().split('T')[0],
        numeroFactura: "",
        cliente: "",
        letraChica: "",
        importeTotal: "",
        ivaPorcentaje: "0",
        fechaVencimiento: new Date().toISOString().split('T')[0],
        saldoAdeudado: "",
        pago1: "",
        pago2: "",
        pago3: "",
        pago4: "",
        pago5: "",
        pago6: "",
        numeroPagos: 1,
        tipoPago: 'efectivo',
        banco: '',
        comprobante: '',
        modoPagosAutomatico: true,
      });
      setEditCuentaId(null);
      setErrorCuentas("");
    } catch (error) {
      console.error('💥 Error creando cuenta por pagar:', error);
      setErrorCuentas(`Error al crear la cuenta por pagar: ${error.message}`);
    }
  };

  // Función para detectar si los pagos son automáticos o manuales
  const detectarModoPagos = (cuenta) => {
    const pagos = [cuenta.pago1, cuenta.pago2, cuenta.pago3, cuenta.pago4, cuenta.pago5, cuenta.pago6]
      .filter(p => p != null && p !== '' && Number(p) > 0)
      .map(p => Number(p));

    if (pagos.length <= 1) return true; // Un solo pago es automáticamente automático

    const total = Number(cuenta.saldoAdeudado || 0);
    const cuotaIgual = total / pagos.length;

    // Verificar si todos los pagos son aproximadamente iguales (con tolerancia de 0.01)
    return pagos.every(pago => Math.abs(pago - cuotaIgual) < 0.01);
  };

  // Editar cuenta por pagar
  const handleEditCuenta = (cuenta) => {
    setEditCuentaId(cuenta.id);
    // Parsear letraChica para meta
    const meta = cuenta.letraChica || '';
    const tipoMatch = meta.match(/Tipo:(efectivo|transferencia)/);
    const bancoMatch = meta.match(/Banco:([^,]+)/);
    const compMatch = meta.match(/Comprobante:([^,]+)/);
    const countPagos = [cuenta.pago1, cuenta.pago2, cuenta.pago3, cuenta.pago4, cuenta.pago5, cuenta.pago6].filter(p => p != null && p !== '' && Number(p) > 0).length || 1;

    // Detectar si los pagos son automáticos o manuales
    const modoAutomatico = detectarModoPagos(cuenta);

    setNuevaCuenta({
      fechaFactura: cuenta.fechaFactura ? cuenta.fechaFactura.split('T')[0] : new Date().toISOString().split('T')[0],
      numeroFactura: cuenta.numeroFactura,
      cliente: cuenta.cliente,
      letraChica: cuenta.letraChica || "",
      importeTotal: cuenta.importeTotal.toString(),
      ivaPorcentaje: (() => { const sub = parseFloat(cuenta.importeTotal||0); const tot = parseFloat(cuenta.saldoAdeudado||0); const iva = tot - sub; return sub > 0 ? ((iva / sub) * 100).toFixed(3) : '0'; })(),
      fechaVencimiento: cuenta.fechaVencimiento ? cuenta.fechaVencimiento.split('T')[0] : new Date().toISOString().split('T')[0],
      saldoAdeudado: cuenta.saldoAdeudado.toString(),
      pago1: cuenta.pago1 ? cuenta.pago1.toString() : "",
      pago2: cuenta.pago2 ? cuenta.pago2.toString() : "",
      pago3: cuenta.pago3 ? cuenta.pago3.toString() : "",
      pago4: cuenta.pago4 ? cuenta.pago4.toString() : "",
      pago5: cuenta.pago5 ? cuenta.pago5.toString() : "",
      pago6: cuenta.pago6 ? cuenta.pago6.toString() : "",
      numeroPagos: cuenta.numeroPagos || countPagos,
      tipoPago: tipoMatch ? tipoMatch[1] : 'efectivo',
      banco: bancoMatch ? bancoMatch[1].trim() : '',
      comprobante: compMatch ? compMatch[1].trim() : '',
      modoPagosAutomatico: modoAutomatico,
    });
  };

  // Actualizar cuenta por pagar
  const handleUpdateCuenta = async (e) => {
    e.preventDefault();
    if (!nuevaCuenta.numeroFactura || !nuevaCuenta.cliente || !nuevaCuenta.importeTotal || nuevaCuenta.importeTotal <= 0) {
      setErrorCuentas("Los campos obligatorios deben estar completos y el subtotal debe ser mayor a 0");
      return;
    }

    // Validar fechas
    if (!nuevaCuenta.fechaFactura) {
      setErrorCuentas("La fecha de factura es obligatoria");
      return;
    }

    if (!nuevaCuenta.fechaVencimiento) {
      setErrorCuentas("La fecha de vencimiento es obligatoria");
      return;
    }

    // Validar que importeTotal sea un número válido
    const importeTotalNum = parseFloat(nuevaCuenta.importeTotal);
    if (isNaN(importeTotalNum) || importeTotalNum <= 0) {
      setErrorCuentas("El subtotal debe ser un número válido mayor a 0");
      return;
    }

    // Validar que el total calculado sea válido
    if (isNaN(totalCalc) || totalCalc <= 0) {
      setErrorCuentas("El total calculado debe ser mayor a 0");
      return;
    }

    // Validar suma de pagos en modo manual
    if (!nuevaCuenta.modoPagosAutomatico) {
      const sumaPagos = [1,2,3,4,5,6].reduce((sum, i) => {
        const pago = nuevaCuenta[`pago${i}`];
        return sum + (pago ? parseFloat(pago) : 0);
      }, 0);

      const totalAdeudado = parseFloat(totalCalc.toFixed(3));
      if (Math.abs(sumaPagos - totalAdeudado) > 0.01) {
        setErrorCuentas(`La suma de los pagos ($${sumaPagos.toFixed(3)}) no coincide con el total adeudado ($${totalAdeudado.toFixed(3)})`);
        return;
      }
    }

    try {
      // Función auxiliar para convertir pagos a números válidos o undefined
      const convertirPago = (pago) => {
        if (!pago || pago === '' || pago === '0') return undefined;
        const num = parseFloat(pago);
        return isNaN(num) || num <= 0 ? undefined : num;
      };

      const meta = `Tipo:${nuevaCuenta.tipoPago}${nuevaCuenta.tipoPago==='transferencia' ? `, Banco:${nuevaCuenta.banco || 'N/A'}`: ''}${nuevaCuenta.comprobante ? `, Comprobante:${nuevaCuenta.comprobante}`:''}`;
      const response = await apiRequest(`/cuentas-por-pagar/${editCuentaId}`, {
        method: 'PUT',
        body: JSON.stringify({
          fechaFactura: nuevaCuenta.fechaFactura,
          numeroFactura: nuevaCuenta.numeroFactura,
          cliente: nuevaCuenta.cliente,
          letraChica: `${meta} | Subtotal: $${parseFloat(nuevaCuenta.importeTotal).toFixed(3)} | IVA: ${parseFloat(nuevaCuenta.ivaPorcentaje || 0).toFixed(2)}% (${ivaMonto.toFixed(2)}$) | #Comprobante: ${nuevaCuenta.numeroComprobante || '-'}`,
          importeTotal: parseFloat(nuevaCuenta.importeTotal),
          fechaVencimiento: nuevaCuenta.fechaVencimiento,
          saldoAdeudado: parseFloat(totalCalc.toFixed(3)),
          pago1: nuevaCuenta.numeroPagos >= 1 ? convertirPago(nuevaCuenta.pago1) : undefined,
          pago2: nuevaCuenta.numeroPagos >= 2 ? convertirPago(nuevaCuenta.pago2) : undefined,
          pago3: nuevaCuenta.numeroPagos >= 3 ? convertirPago(nuevaCuenta.pago3) : undefined,
          pago4: nuevaCuenta.numeroPagos >= 4 ? convertirPago(nuevaCuenta.pago4) : undefined,
          pago5: nuevaCuenta.numeroPagos >= 5 ? convertirPago(nuevaCuenta.pago5) : undefined,
          pago6: nuevaCuenta.numeroPagos >= 6 ? convertirPago(nuevaCuenta.pago6) : undefined,
          empresaId: empresaSeleccionada?.id,
        }),
      });

      if (!response.ok) throw new Error((await response.json()).error || 'Error al actualizar cuenta por pagar');

      await cargarCuentasPorPagar();
      setNuevaCuenta({
        fechaFactura: new Date().toISOString().split('T')[0],
        numeroFactura: "",
        cliente: "",
        letraChica: "",
        importeTotal: "",
        ivaPorcentaje: "0",
        fechaVencimiento: new Date().toISOString().split('T')[0],
        saldoAdeudado: "",
        pago1: "",
        pago2: "",
        pago3: "",
        pago4: "",
        pago5: "",
        pago6: "",
        numeroPagos: 1,
        tipoPago: 'efectivo',
        banco: '',
        comprobante: '',
        modoPagosAutomatico: true,
      });
      setEditCuentaId(null);
      setErrorCuentas("");
    } catch (error) {
      console.error('Error actualizando cuenta por pagar:', error);
      setErrorCuentas('Error al actualizar la cuenta por pagar');
    }
  };

  // Eliminar cuenta por pagar
  const handleDeleteCuenta = async (id) => {
    if (!window.confirm('¿Estás seguro de que quieres eliminar esta cuenta por pagar?')) return;
    try {
      const response = await apiRequest(`/cuentas-por-pagar/${id}`, { method: 'DELETE' });
      if (!response.ok) throw new Error('Error al eliminar cuenta por pagar');
      await cargarCuentasPorPagar();
    } catch (error) {
      console.error('Error eliminando cuenta por pagar:', error);
      setErrorCuentas('Error al eliminar la cuenta por pagar');
    }
  };

  // Cancelar edición de cuenta
  const handleCancelEditCuenta = () => {
    setEditCuentaId(null);
    setNuevaCuenta({
      fechaFactura: new Date().toISOString().split('T')[0],
      numeroFactura: "",
      cliente: "",
      letraChica: "",
      importeTotal: "",
      ivaPorcentaje: "0",
      fechaVencimiento: new Date().toISOString().split('T')[0],
      saldoAdeudado: "",
      pago1: "",
      pago2: "",
      pago3: "",
      pago4: "",
      pago5: "",
      pago6: "",
      numeroPagos: 1,
      tipoPago: 'efectivo',
      banco: '',
      comprobante: '',
      modoPagosAutomatico: true,
    });
    setErrorCuentas("");
  };

  return (
    <div className="egresos-container">
      {/* Navegación de pestañas */}
      <div className="tabs is-centered is-boxed mb-5">
        <ul>
          <li>
            <a href="/dashboard/contabilidad/ingresos">
              <span className="icon is-small"><i className="fas fa-dollar-sign" aria-hidden="true"></i></span>
              <span>Cuentas por Cobrar</span>
            </a>
          </li>
          <li className="is-active">
            <a>
              <span className="icon is-small"><i className="fas fa-minus" aria-hidden="true"></i></span>
              <span>Cuentas por Pagar</span>
            </a>
          </li>
          <li>
            <a href="/dashboard/contabilidad/patrimonio">
              <span className="icon is-small"><i className="fas fa-chart-pie" aria-hidden="true"></i></span>
              <span>Patrimonio</span>
            </a>
          </li>
          <li>
            <a href="/dashboard/contabilidad/calculo-costos">
              <span className="icon is-small"><i className="fas fa-calculator" aria-hidden="true"></i></span>
              <span>Cálculo de Costos</span>
            </a>
          </li>
          <li>
            <a href="/dashboard/contabilidad/resumen">
              <span className="icon is-small"><i className="fas fa-chart-bar" aria-hidden="true"></i></span>
              <span>Resumen</span>
            </a>
          </li>
        </ul>
      </div>

      <h1 className="egresos-title">Cuentas por Pagar</h1>

      {/* Indicador de empresa */}
      {empresaSeleccionada && (
        <div className="notification is-info is-light has-text-centered mb-4">
          <p className="has-text-weight-semibold">
            📊 Visualizando datos de: <strong>{empresaSeleccionada.nombre || 'Empresa'}</strong>
          </p>
          {esConexAdmin && (
            <p className="is-size-7">
              Puedes cambiar de empresa usando el selector en la parte superior
            </p>
          )}
        </div>
      )}

      {/* Filtro por Fecha */}
      <div className="box mb-4" style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
        <label className="label" style={{ marginBottom: 0 }}>Filtrar por fecha:</label>
        <input
          className="input"
          type="date"
          value={filtroFecha}
          onChange={(e) => setFiltroFecha(e.target.value)}
          style={{ maxWidth: 220 }}
        />
        <button className="button is-light" onClick={() => setFiltroFecha(new Date().toISOString().split('T')[0])}>Hoy</button>
        <button className="button is-small is-light" onClick={() => {
          const datosAFiltrar = filtroFecha ? cuentasPorPagarFiltradas : cuentasPorPagar;
          const rows = datosAFiltrar.map(c => {
            const sub = parseFloat(c.importeTotal||0);
            const tot = parseFloat(c.saldoAdeudado||sub);
            const iva = tot - sub;
            // parse meta
            const meta = c.letraChica || '';
            const tipo = (meta.match(/Tipo:(efectivo|transferencia)/) || [,''])[1];
            const banco = (meta.match(/Banco:([^,]+)/) || [,''])[1] || '';
            const comp = (meta.match(/Comprobante:([^,]+)/) || [,''])[1] || '';
            return {
              FechaFactura: c.fechaFactura?.split('T')[0] || '',
              NumeroFactura: c.numeroFactura || '',
              Cliente: c.cliente || '',
              Descripcion: c.letraChica || '',
              Subtotal: sub,
              IVA: iva,
              Total: tot,
              TipoPago: tipo,
              Banco: banco,
              Comprobante: comp,
              FechaVencimiento: c.fechaVencimiento?.split('T')[0] || '',
            };
          });
          const nombreArchivo = filtroFecha ? `cuentas_por_pagar_${filtroFecha}.xlsx` : 'cuentas_por_pagar_todas.xlsx';
          const ws = XLSX.utils.json_to_sheet(rows);
          const wb = XLSX.utils.book_new();
          XLSX.utils.book_append_sheet(wb, ws, 'CuentasPagar');
          XLSX.writeFile(wb, nombreArchivo);
        }}>Descargar Excel ({filtroFecha ? 'Filtradas' : 'Todas'})</button>
        <button
          className="button is-gold ml-2"
          onClick={() => {
            const datosAFiltrar = filtroFecha ? egresosFiltrados : egresos;
            const rows = datosAFiltrar.map(e => {
              const matchSubtotal = e.descripcion?.match(/Subtotal: \$(\d+\.?\d*)/);
              const subtotal = matchSubtotal ? parseFloat(matchSubtotal[1]) : 0;
              const matchIva = e.descripcion?.match(/IVA: (\d+\.?\d*)%/);
              const iva = matchIva ? parseFloat(matchIva[1]) : 0;
              const total = parseFloat(e.monto || 0);
              const descripcion = e.descripcion?.split('|')[0]?.trim() || e.descripcion;
              const matchComp = e.descripcion?.match(/#Comprobante: ([^|]+)/);
              const numeroComprobante = matchComp ? matchComp[1].trim() : '';
              return {
                Fecha: e.fecha ? new Date(e.fecha).toLocaleDateString('es-ES') : '',
                Descripcion: descripcion,
                Subtotal: subtotal.toFixed(3),
                IVA: `${iva.toFixed(2)}%`,
                Total: total.toFixed(2),
                TipoPago: e.tipoPago || '',
                Banco: e.banco || '',
                Comprobante: numeroComprobante,
                Categoria: e.categoria || '',
                EmpresaId: e.empresaId || '',
              };
            });
            const nombreArchivo = filtroFecha ? `egresos_${filtroFecha}.xlsx` : 'egresos_todos.xlsx';
            const ws = XLSX.utils.json_to_sheet(rows);
            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws, 'Egresos');
            XLSX.writeFile(wb, nombreArchivo);
          }}
        >
          <span className="icon">
            <i className="fas fa-file-excel"></i>
          </span>
          <span>Excel Egresos</span>
        </button>
      </div>

      {/* Formulario para crear/editar egreso */}
      <div className="egreso-form">
        {/* Título visible para la sección de egresos */}
        <h2 className="title is-4 has-text-darkblue" style={{ marginBottom: '0.75rem' }}>EGRESOS</h2>
        {error && <p className="notification is-danger has-text-centered">{error}</p>}
        <form onSubmit={editEgresoId !== null ? handleUpdateEgreso : handleAddEgreso} className="mb-4">
          <div className="form-row" style={{ display: 'flex', flexWrap: 'wrap', gap: '12px' }}>
            <div className="form-group" style={{ flex: '1 1 300px' }}>
              <label className="label">Descripción</label>
              <input
                className="input"
                type="text"
                placeholder="Descripción del egreso"
                value={nuevoEgreso.descripcion}
                onChange={(e) => setNuevoEgreso({ ...nuevoEgreso, descripcion: e.target.value })}
              />
            </div>
            <div className="form-group" style={{ flex: '1 1 150px' }}>
              <label className="label">Subtotal</label>
              <input
                className="input"
                type="number"
                min="0"
                step="0.001"
                placeholder="$0.00"
                value={nuevoEgreso.subtotal}
                onChange={(e) => setNuevoEgreso({ ...nuevoEgreso, subtotal: e.target.value })}
              />
            </div>
            <div className="form-group" style={{ flex: '1 1 150px' }}>
              <label className="label">IVA (%)</label>
              <input
                className="input"
                type="number"
                min="0"
                step="0.01"
                placeholder="0.00"
                value={nuevoEgreso.iva}
                onChange={(e) => setNuevoEgreso({ ...nuevoEgreso, iva: e.target.value })}
              />
            </div>
            <div className="form-group" style={{ flex: '1 1 150px' }}>
              <label className="label">Total (auto)</label>
              <input
                className="input"
                type="number"
                readOnly
                value={(() => { const s = parseFloat(nuevoEgreso.subtotal || 0); const iva = parseFloat(nuevoEgreso.iva || 0); return (s + s * (iva / 100)).toFixed(2); })()}
              />
            </div>
            <div className="form-group" style={{ flex: '1 1 150px' }}>
              <label className="label"># Comprobante</label>
              <input
                className="input"
                type="text"
                placeholder="Número de comprobante"
                value={nuevoEgreso.numeroComprobante}
                onChange={(e) => setNuevoEgreso({ ...nuevoEgreso, numeroComprobante: e.target.value })}
              />
            </div>
            <div className="form-group" style={{ flex: '1 1 150px' }}>
              <label className="label">Tipo de Pago</label>
              <div className="select is-fullwidth">
                <select
                  value={nuevoEgreso.tipoPago}
                  onChange={(e) => setNuevoEgreso({ 
                    ...nuevoEgreso, 
                    tipoPago: e.target.value,
                    banco: e.target.value === 'efectivo' ? '' : nuevoEgreso.banco
                  })}
                >
                  <option value="efectivo">Efectivo</option>
                  <option value="transferencia">Transferencia</option>
                </select>
              </div>
            </div>
            <div className="form-group" style={{ flex: '1 1 150px' }}>
              <label className="label">Categoría</label>
              <div className="select is-fullwidth">
                <select
                  value={nuevoEgreso.categoria}
                  onChange={(e) => setNuevoEgreso({ ...nuevoEgreso, categoria: e.target.value })}
                >
                  <option value="productos">Productos</option>
                  <option value="costo_fijo">Costo Fijo</option>
                  <option value="pago_prestamo">Pago de Préstamo</option>
                </select>
              </div>
            </div>
            <div className="form-group" style={{ flex: '1 1 150px' }}>
              <label className="label">Fecha</label>
              <input
                className="input"
                type="date"
                value={nuevoEgreso.fecha}
                onChange={(e) => setNuevoEgreso({ ...nuevoEgreso, fecha: e.target.value })}
              />
            </div>
            {nuevoEgreso.tipoPago === 'transferencia' && (
              <div className="form-group" style={{ flex: '1 1 150px' }}>
                <label className="label">Banco</label>
                <input
                  className="input"
                  type="text"
                  placeholder="Escribe el banco"
                  value={nuevoEgreso.banco}
                  onChange={(e) => setNuevoEgreso({ ...nuevoEgreso, banco: e.target.value })}
                />
              </div>
            )}
          </div>
          <div className="form-actions mt-4">
            <button type="submit" className="button is-gold" aria-label={editEgresoId !== null ? "Actualizar egreso" : "Agregar egreso"}>
              {editEgresoId !== null ? "Actualizar Egreso" : "Agregar Egreso"}
            </button>
            {editEgresoId !== null && (
              <button
                type="button"
                className="button is-light ml-2"
                onClick={handleCancelEdit}
                aria-label="Cancelar edición"
              >
                Cancelar
              </button>
            )}
          </div>
        </form>

        {/* Tabla de Egresos Diarios */}
        {loading ? (
          <p className="has-text-centered">Cargando egresos...</p>
        ) : egresosFiltrados.length === 0 ? (
          <p className="has-text-centered">No hay egresos registrados</p>
        ) : (
          <div className="table-container mt-4">
            <table className="table is-fullwidth is-striped" style={{ 
                minWidth: '1400px', 
                width: 'max-content',
                marginBottom: '0'
              }}>
              <thead>
                <tr>
                  <th>Fecha</th>
                  <th>Descripción</th>
                  <th>Subtotal</th>
                  <th>IVA (%)</th>
                  <th>Total</th>
                  <th>Tipo de Pago</th>
                  <th>Banco</th>
                  <th># Comprobante</th>
                  <th>Categoría</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {egresosFiltrados.map((egreso) => {
                  const matchSubtotal = egreso.descripcion?.match(/Subtotal: \$(\d+\.?\d*)/);
                  const subtotal = matchSubtotal ? parseFloat(matchSubtotal[1]) : 0;
                  const matchIva = egreso.descripcion?.match(/IVA: (\d+\.?\d*)%/);
                  const iva = matchIva ? parseFloat(matchIva[1]) : 0;
                  const total = parseFloat(egreso.monto || 0);
                  const descripcion = egreso.descripcion?.split('|')[0]?.trim() || egreso.descripcion;
                  const matchComp = egreso.descripcion?.match(/#Comprobante: ([^|]+)/);
                  const numeroComprobante = matchComp ? matchComp[1].trim() : '';
                  return (
                    <tr key={egreso.id}>
                      <td>{
                        egreso.fecha
                          ? egreso.fecha.split('T')[0]
                          : 'N/A'
                      }</td>
                      <td>{descripcion}</td>
                      <td>${subtotal.toFixed(3)}</td>
                      <td>${iva.toFixed(2)}%</td>
                      <td>${total.toFixed(2)}</td>
                      <td>{egreso.tipoPago || '-'}</td>
                      <td>{egreso.banco || '-'}</td>
                      <td>{numeroComprobante}</td>
                      <td>{egreso.categoria || '-'}</td>
                      <td>
                        <button className="button is-small is-info mr-2" onClick={() => handleEditEgreso(egreso)} title="Editar">
                          <span className="icon"><i className="fas fa-edit"></i></span>
                        </button>
                        <button
                          className="button is-small is-danger"
                          onClick={() => {
                            handleDeleteEgreso(egreso.id);
                          }}
                          title={`Eliminar egreso - Empresa: ${egreso.empresaId}`}
                        >
                          <span className="icon"><i className="fas fa-trash"></i></span>
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Tabla de resumen azul para Egresos */}
      <div className="box mt-5" style={{ background: '#eaf3ff' }}>
        <h3 className="subtitle is-6 has-text-centered has-text-link mb-3">
          Resumen de Egresos {filtroFecha ? `del Día (${filtroFecha})` : 'Total'}
        </h3>
        <div className="is-flex is-justify-content-space-around is-flex-wrap-wrap has-text-link">
          <p className="has-text-weight-semibold" style={{ fontSize: '1.05rem' }}>
            Total Egresos: ${egresosFiltrados.reduce((total, egreso) => total + parseFloat(egreso.monto || 0), 0).toFixed(2)}
          </p>
          <p className="has-text-weight-semibold" style={{ fontSize: '1.05rem' }}>
            Efectivo: ${egresosFiltrados.filter(egreso => egreso.tipoPago === 'efectivo').reduce((total, egreso) => total + parseFloat(egreso.monto || 0), 0).toFixed(2)}
          </p>
          <p className="has-text-weight-semibold" style={{ fontSize: '1.05rem' }}>
            Transferencias: ${egresosFiltrados.filter(egreso => egreso.tipoPago === 'transferencia').reduce((total, egreso) => total + parseFloat(egreso.monto || 0), 0).toFixed(2)}
          </p>
        </div>
      </div>

      {/* Formulario para crear/editar cuenta por pagar */}
      <div className="egreso-form mt-6">
        <h2 className="subtitle has-text-centered has-text-darkblue" style={{ fontSize: '1.25rem' }}>
          Cuentas por pagar
        </h2>
        {errorCuentas && <p className="notification is-danger has-text-centered">{errorCuentas}</p>}
        <form onSubmit={editCuentaId !== null ? handleUpdateCuenta : handleAddCuenta} className="mb-4">
          <div className="form-row">
            <div className="form-group">
              <label className="label">Fecha Factura</label>
              <input
                className="input"
                type="date"
                value={nuevaCuenta.fechaFactura}
                onChange={(e) => setNuevaCuenta({ ...nuevaCuenta, fechaFactura: e.target.value })}
              />
            </div>
            <div className="form-group">
              <label className="label">N° Factura</label>
              <input
                className="input"
                type="text"
                placeholder="Número de factura"
                value={nuevaCuenta.numeroFactura}
                onChange={(e) => setNuevaCuenta({ ...nuevaCuenta, numeroFactura: e.target.value })}
              />
            </div>
            <div className="form-group">
              <label className="label">Cliente</label>
              <input
                className="input"
                type="text"
                placeholder="Nombre del cliente"
                value={nuevaCuenta.cliente}
                onChange={(e) => setNuevaCuenta({ ...nuevaCuenta, cliente: e.target.value })}
              />
            </div>
            <div className="form-group">
              <label className="label">Descripción</label>
              <input className="input" type="text" placeholder="Detalle" value={nuevaCuenta.letraChica} onChange={(e) => setNuevaCuenta({ ...nuevaCuenta, letraChica: e.target.value })} />
            </div>
            <div className="form-group">
              <label className="label">Subtotal</label>
              <input
                className="input"
                type="number"
                placeholder="$0.00"
                value={nuevaCuenta.importeTotal}
                onChange={(e) => setNuevaCuenta({ ...nuevaCuenta, importeTotal: e.target.value })}
              />
            </div>
            <div className="form-group">
              <label className="label">IVA (%)</label>
              <input className="input" type="number" step="0.01" value={nuevaCuenta.ivaPorcentaje} onChange={(e) => setNuevaCuenta({ ...nuevaCuenta, ivaPorcentaje: e.target.value })} />
            </div>
            <div className="form-group">
              <label className="label">Total (auto)</label>
              <input className="input" type="number" readOnly value={totalCalc.toFixed(3)} />
            </div>
            <div className="form-group">
              <label className="label">Fecha Vencimiento</label>
              <input
                className="input"
                type="date"
                value={nuevaCuenta.fechaVencimiento}
                onChange={(e) => setNuevaCuenta({ ...nuevaCuenta, fechaVencimiento: e.target.value })}
              />
            </div>
            <div className="form-group">
              <label className="label">Tipo de Pago</label>
              <div className="select is-fullwidth">
                <select value={nuevaCuenta.tipoPago} onChange={(e) => setNuevaCuenta({ ...nuevaCuenta, tipoPago: e.target.value, banco: e.target.value==='efectivo'?'':nuevaCuenta.banco })}>
                  <option value="efectivo">Efectivo</option>
                  <option value="transferencia">Transferencia</option>
                </select>
              </div>
            </div>
            {nuevaCuenta.tipoPago === 'transferencia' && (
              <div className="form-group">
                <label className="label">Banco</label>
                <input className="input" type="text" placeholder="Escribe el banco" value={nuevaCuenta.banco} onChange={(e) => setNuevaCuenta({ ...nuevaCuenta, banco: e.target.value })} />
              </div>
            )}
            <div className="form-group">
              <label className="label"># Comprobante</label>
              <input className="input" type="text" placeholder="Número de comprobante" value={nuevaCuenta.comprobante} onChange={(e) => setNuevaCuenta({ ...nuevaCuenta, comprobante: e.target.value })} />
            </div>
            <div className="form-group">
              <label className="label">Número de Pagos</label>
              <div className="select is-fullwidth">
                <select
                  value={nuevaCuenta.numeroPagos || 1}
                  onChange={(e) => setNuevaCuenta({ ...nuevaCuenta, numeroPagos: parseInt(e.target.value || '1') })}
                >
                  {[1,2,3,4,5,6].map(n => (
                    <option key={n} value={n}>{n}</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="form-group">
              <label className="label">Modo de Pagos</label>
              <div className="select is-fullwidth">
                <select
                  value={nuevaCuenta.modoPagosAutomatico ? 'automatico' : 'manual'}
                  onChange={(e) => setNuevaCuenta({ ...nuevaCuenta, modoPagosAutomatico: e.target.value === 'automatico' })}
                >
                  <option value="automatico">Automático (iguales)</option>
                  <option value="manual">Manual (personalizado)</option>
                </select>
              </div>
            </div>
            {[...Array(nuevaCuenta.numeroPagos || 1)].map((_, i) => (
              <div className="form-group" key={`pago${i+1}`}>
                <label className="label">{`Pago ${i+1}`}</label>
                <input
                  className="input"
                  type="number"
                  step="0.001"
                  placeholder="$0.00"
                  value={nuevaCuenta[`pago${i+1}`] || ""}
                  onChange={(e) => {
                    if (!nuevaCuenta.modoPagosAutomatico) {
                      setNuevaCuenta({ ...nuevaCuenta, [`pago${i+1}`]: e.target.value });
                    }
                  }}
                  readOnly={nuevaCuenta.modoPagosAutomatico}
                  style={{
                    backgroundColor: nuevaCuenta.modoPagosAutomatico ? '#f5f5f5' : 'white',
                    cursor: nuevaCuenta.modoPagosAutomatico ? 'not-allowed' : 'text'
                  }}
                />
              </div>
            ))}
            {!nuevaCuenta.modoPagosAutomatico && (nuevaCuenta.numeroPagos || 1) > 1 && (
              <div className="form-group">
                <label className="label">Suma de Pagos</label>
                <div className="control">
                  <input
                    className="input"
                    type="text"
                    value={`$${(() => {
                      const suma = [1,2,3,4,5,6].reduce((sum, i) => {
                        const pago = nuevaCuenta[`pago${i}`];
                        return sum + (pago ? parseFloat(pago) : 0);
                      }, 0);
                      const total = parseFloat(totalCalc.toFixed(3));
                      const diferencia = Math.abs(suma - total);
                      return `${suma.toFixed(3)} ${diferencia > 0.01 ? `(Diferencia: $${diferencia.toFixed(3)})` : '(✓ Balanceado)'}`;
                    })()}`}
                    readOnly
                    style={{
                      backgroundColor: (() => {
                        const suma = [1,2,3,4,5,6].reduce((sum, i) => {
                          const pago = nuevaCuenta[`pago${i}`];
                          return sum + (pago ? parseFloat(pago) : 0);
                        }, 0);
                        const total = parseFloat(totalCalc.toFixed(3));
                        const diferencia = Math.abs(suma - total);
                        return diferencia > 0.01 ? '#ffe6e6' : '#e6ffe6';
                      })()
                    }}
                  />
                </div>
              </div>
            )}
          </div>
          <div className="form-actions">
            <button type="submit" className="button is-gold">
              {editCuentaId !== null ? "Actualizar Cuenta" : "Agregar Cuenta"}
            </button>
            {editCuentaId !== null && (
              <button
                type="button"
                className="button is-light"
                onClick={handleCancelEditCuenta}
              >
                Cancelar
              </button>
            )}
          </div>
        </form>
      </div>
      {/* Cuentas por pagar - listado */}
      {loadingCuentas ? (
        <p className="has-text-centered">Cargando cuentas por pagar...</p>
      ) : cuentasPorPagarFiltradas.length === 0 ? (
        <p className="has-text-centered">No hay cuentas por pagar registradas</p>
      ) : (
        <div>
          {/* Resumen de totales */}
          <div className="notification is-info is-light mb-3" style={{ fontSize: '1.05rem' }}>
            <div className="columns is-multiline">
              <div className="column is-4"><strong>Total Facturas:</strong> <span className="has-text-weight-bold">{cuentasPorPagarFiltradas.length}</span></div>
              <div className="column is-4"><strong>Total a Pagar:</strong> <span className="has-text-weight-bold">${(Math.ceil(cuentasPorPagarFiltradas.reduce((sum, c) => sum + (c.saldoAdeudado || 0), 0) * 100) / 100).toFixed(2)}</span></div>
              <div className="column is-4"><strong>Vencidas:</strong> <span className="has-text-weight-bold">{cuentasPorPagarFiltradas.filter(c => new Date(c.fechaVencimiento) < new Date()).length}</span></div>
            </div>
          </div>

          <div className="table-container mt-2">
            <table className="table is-fullwidth is-striped">
              <thead>
                <tr>
                  <th>Fecha Factura</th>
                  <th>N° Factura</th>
                  <th>Cliente</th>
                  <th>Letra Chica</th>
                  <th>Fecha Vencimiento</th>
                  <th>Saldo Adeudado</th>
                  <th>P1</th>
                  <th>P2</th>
                  <th>P3</th>
                  <th>P4</th>
                  <th>P5</th>
                  <th>P6</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {cuentasPorPagarFiltradas.map((cuenta) => (
                  <tr key={cuenta.id}>
                    <td style={{ fontSize: '11px' }}>{
                      cuenta.fechaFactura
                        ? cuenta.fechaFactura.split('T')[0]
                        : 'N/A'
                    }</td>
                    <td style={{ fontSize: '11px' }}>{cuenta.numeroFactura}</td>
                    <td style={{ fontSize: '11px', maxWidth: '120px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={cuenta.cliente}>{cuenta.cliente}</td>
                    <td style={{ fontSize: '11px' }}>{cuenta.letraChica || '-'}</td>
                    <td style={{ fontSize: '11px' }}>{
                      cuenta.fechaVencimiento
                        ? cuenta.fechaVencimiento.split('T')[0]
                        : 'N/A'
                    }</td>
                    <td style={{ fontSize: '11px', fontWeight: 'bold' }}>${(Math.ceil(cuenta.saldoAdeudado * 100) / 100).toFixed(2)}</td>
                    <td className="pago-column" style={{ fontSize: '11px' }}>{cuenta.pago1 != null ? `$${(Math.ceil(Number(cuenta.pago1) * 100) / 100).toFixed(2)}` : ''}</td>
                    <td className="pago-column" style={{ fontSize: '11px' }}>{cuenta.pago2 != null ? `$${(Math.ceil(Number(cuenta.pago2) * 100) / 100).toFixed(2)}` : ''}</td>
                    <td className="pago-column" style={{ fontSize: '11px' }}>{cuenta.pago3 != null ? `$${(Math.ceil(Number(cuenta.pago3) * 100) / 100).toFixed(2)}` : ''}</td>
                    <td className="pago-column" style={{ fontSize: '11px' }}>{cuenta.pago4 != null ? `$${(Math.ceil(Number(cuenta.pago4) * 100) / 100).toFixed(2)}` : ''}</td>
                    <td className="pago-column" style={{ fontSize: '11px' }}>{cuenta.pago5 != null ? `$${(Math.ceil(Number(cuenta.pago5) * 100) / 100).toFixed(2)}` : ''}</td>
                    <td className="pago-column" style={{ fontSize: '11px' }}>{cuenta.pago6 != null ? `$${(Math.ceil(Number(cuenta.pago6) * 100) / 100).toFixed(2)}` : ''}</td>
                    <td style={{ fontSize: '11px' }}>
                      <button
                        className="button is-small is-info mr-1"
                        onClick={() => handleEditCuenta(cuenta)}
                        style={{ fontSize: '10px', padding: '4px 8px' }}
                        aria-label="Editar cuenta"
                        title="Editar"
                      >
                        <span className="icon"><i className="fas fa-edit"></i></span>
                      </button>
                      <button
                        className="button is-small is-danger"
                        onClick={() => handleDeleteCuenta(cuenta.id)}
                        style={{ fontSize: '10px', padding: '4px 8px' }}
                        aria-label="Eliminar cuenta"
                        title="Eliminar"
                      >
                        <span className="icon"><i className="fas fa-trash"></i></span>
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Modal de confirmación para crear en todas las empresas */}
      {showAllEmpresasModal && (
        <div className="modal is-active">
          <div className="modal-background" onClick={() => { setShowAllEmpresasModal(false); pendingCreationRef.current = null; }}></div>
          <div className="modal-card">
            <header className="modal-card-head" style={{ backgroundColor: '#1a1a2e' }}>
              <p className="modal-card-title has-text-warning">⚠️ Confirmar creación para todas las empresas</p>
              <button className="delete" aria-label="close" onClick={() => { setShowAllEmpresasModal(false); pendingCreationRef.current = null; }}></button>
            </header>
            <section className="modal-card-body">
              <div className="content">
                <p className="has-text-weight-semibold is-size-5">
                  ¿Estás seguro de que deseas crear este registro para <strong>todas las empresas</strong>?
                </p>
                <p>
                  Se creará un registro en cada una de las <strong>{empresas?.length || 0}</strong> empresas registradas actualmente:
                </p>
                <ul>
                  {empresas?.map(emp => (
                    <li key={emp.id}>{emp.nombre}</li>
                  ))}
                </ul>
                <div className="notification is-warning is-light">
                  <strong>Nota:</strong> Esta acción creará {empresas?.length || 0} registros, uno por cada empresa.
                </div>
              </div>
            </section>
            <footer className="modal-card-foot" style={{ justifyContent: 'flex-end' }}>
              <button className="button" onClick={() => { setShowAllEmpresasModal(false); pendingCreationRef.current = null; }}>
                No, Cancelar
              </button>
              <button className="button is-warning has-text-dark" onClick={async () => {
                const pending = pendingCreationRef.current;
                if (!pending || !empresas) {
                  setShowAllEmpresasModal(false);
                  return;
                }

                try {
                  if (pending.type === 'egreso') {
                    const subtotalNum = parseFloat(nuevoEgreso.subtotal);
                    const ivaPct = parseFloat(nuevoEgreso.iva || 0);
                    const ivaNum = subtotalNum * (ivaPct / 100);
                    const totalNum = subtotalNum + ivaNum;

                    for (const empresa of empresas) {
                      const egresoData = {
                        descripcion: `${nuevoEgreso.descripcion} | Subtotal: $${subtotalNum.toFixed(3)} | IVA: ${ivaPct.toFixed(2)}% ($${ivaNum.toFixed(2)}) | #Comprobante: ${nuevoEgreso.numeroComprobante || '-'}`,
                        monto: parseFloat(totalNum.toFixed(2)),
                        fecha: new Date(nuevoEgreso.fecha).toISOString(),
                        tipoPago: nuevoEgreso.tipoPago,
                        banco: nuevoEgreso.banco || null,
                        categoria: nuevoEgreso.categoria,
                        empresaId: empresa.id,
                      };
                      await apiRequest('/egresos', { method: 'POST', body: JSON.stringify(egresoData) });
                    }
                    await cargarEgresos();
                    setNuevoEgreso({ descripcion: '', subtotal: '', iva: '', numeroComprobante: '', tipoPago: 'efectivo', banco: '', categoria: 'productos', fecha: new Date().toISOString().split('T')[0] });
                    setError('');
                  } else if (pending.type === 'cuentaPorPagar') {
                    const meta = `Tipo:${nuevaCuenta.tipoPago}${nuevaCuenta.tipoPago === 'transferencia' ? `, Banco:${nuevaCuenta.banco || 'N/A'}` : ''}${nuevaCuenta.comprobante ? `, Comprobante:${nuevaCuenta.comprobante}` : ''}`;
                    const convertirPago = (pago) => { if (!pago || pago === '' || pago === '0') return undefined; const num = parseFloat(pago); return isNaN(num) || num <= 0 ? undefined : num; };

                    for (const empresa of empresas) {
                      const datosAEnviar = {
                        fechaFactura: nuevaCuenta.fechaFactura,
                        numeroFactura: nuevaCuenta.numeroFactura,
                        cliente: nuevaCuenta.cliente,
                        letraChica: `${meta} | Subtotal: $${parseFloat(nuevaCuenta.importeTotal).toFixed(3)} | IVA: ${parseFloat(nuevaCuenta.ivaPorcentaje || 0).toFixed(2)}% (${ivaMonto.toFixed(2)}$) | #Comprobante: ${nuevaCuenta.numeroComprobante || '-'}`,
                        importeTotal: parseFloat(nuevaCuenta.importeTotal),
                        fechaVencimiento: nuevaCuenta.fechaVencimiento,
                        saldoAdeudado: parseFloat(totalCalc.toFixed(3)),
                        pago1: convertirPago(nuevaCuenta.pago1),
                        pago2: convertirPago(nuevaCuenta.pago2),
                        pago3: convertirPago(nuevaCuenta.pago3),
                        pago4: convertirPago(nuevaCuenta.pago4),
                        pago5: convertirPago(nuevaCuenta.pago5),
                        pago6: convertirPago(nuevaCuenta.pago6),
                        empresaId: empresa.id,
                      };
                      await apiRequest('/cuentas-por-pagar', { method: 'POST', body: JSON.stringify(datosAEnviar) });
                    }
                    await cargarCuentasPorPagar();
                    setNuevaCuenta({ fechaFactura: new Date().toISOString().split('T')[0], numeroFactura: '', cliente: '', letraChica: '', importeTotal: '', ivaPorcentaje: '0', fechaVencimiento: new Date().toISOString().split('T')[0], saldoAdeudado: '', pago1: '', pago2: '', pago3: '', pago4: '', pago5: '', pago6: '', numeroPagos: 1, tipoPago: 'efectivo', banco: '', comprobante: '', modoPagosAutomatico: true });
                    setErrorCuentas('');
                  }
                } catch (err) {
                  console.error('Error creando para todas las empresas:', err);
                  setError('Error al crear para todas las empresas');
                } finally {
                  setShowAllEmpresasModal(false);
                  pendingCreationRef.current = null;
                }
              }}>
                Sí, Crear para todas
              </button>
            </footer>
          </div>
        </div>
      )}
    </div>
  );
}
